/**
 * InfinitSales - Company Service
 *
 * API client para Conta Empresa. Encapsula chamadas a /api/companies/* e
 * normaliza erros do backend (que vêm com `{code, message}` no detail) para
 * o frontend poder branchear em códigos como `plan_upgrade_required` e
 * `seat_limit_reached` sem ter que parsear strings.
 */
import api from './api';
import requestCache from './requestCache';

// Toda mutação de empresa derruba as leituras cacheadas ('company:*') — os
// refetches pós-mutação (ex.: MembersTab recarrega categorias após criar)
// voltam a bater no servidor e enxergam o dado novo na hora.
const bustCompanyCache = () => requestCache.invalidate('company:');

/**
 * Extract `{code, message}` from a backend error response.
 * Backend convention: HTTPException(detail={"code": "...", "message": "..."}).
 * Falls back to a generic shape when the response doesn't follow the convention.
 */
function parseCompanyError(error) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && detail.code) {
    // meta: cadeiras usadas/incluídas e preço da adicional (bloqueios de cadeira).
    return { code: detail.code, message: detail.message || 'Erro', meta: detail.meta || null };
  }
  if (typeof detail === 'string') {
    return { code: 'company_error', message: detail };
  }
  return {
    code: 'unknown_error',
    message: error?.message || 'Erro inesperado',
  };
}

const companyService = {
  parseCompanyError,

  /**
   * Get Conta Empresa limits/pricing (public — used by UpgradeModal copy).
   * Returns { max_total_users, extra_seat_price_brl, required_plan_tier, extra_seat_available }.
   */
  async getLimits() {
    // Pricing/limites estáticos do plano — imutável na sessão (30min).
    return requestCache.cachedGet(
      'company:limits',
      async () => {
        const response = await api.get('/api/companies/limits');
        return response.data?.data;
      },
      { ttl: 30 * 60_000 },
    );
  },

  /**
   * Get the current user's company (or null if individual mode).
   * Returns { company, role } or null.
   */
  async getMyCompany() {
    // Catálogo (nome/settings/categorias): 5 consumidores independentes
    // (EmpresaDashboard, MemberWelcomeModal, TagsKanbanPage, CalendarPage,
    // drawers de roleta) refaziam o GET a cada mount — vira 1 por janela.
    return requestCache.cachedGet(
      'company:me',
      async () => {
        const response = await api.get('/api/companies/me');
        return response.data?.data;
      },
      { ttl: 5 * 60_000 },
    );
  },

  /**
   * Promote the current user account to a Conta Empresa.
   * Throws on plan-gate failure (code: 'plan_upgrade_required') or other errors.
   */
  async promote(name) {
    const response = await api.post('/api/companies/promote', { name });
    bustCompanyCache();
    return response.data?.data;
  },

  /**
   * Update company name/settings (admin-only).
   */
  async updateCompany(companyId, payload) {
    const response = await api.patch(`/api/companies/${companyId}`, payload);
    bustCompanyCache();
    return response.data?.data;
  },

  /**
   * Delete company (admin-only). Backend blocks if there are remaining members.
   */
  async deleteCompany(companyId) {
    const response = await api.delete(`/api/companies/${companyId}`);
    bustCompanyCache();
    return response.data?.data;
  },

  /**
   * List members of a company (admin-only).
   * Returns { data: [members], meta: { total, max_total_users } }.
   */
  async listMembers(companyId) {
    const response = await api.get(`/api/companies/${companyId}/members`);
    return response.data;
  },

  /**
   * List company workspaces (Etapa 2 pool view — any active member).
   * Returns { data: [{ user_id, name, email, company_role, default_channel, is_self, preview }],
   *           meta: { total, self_user_id } }.
   */
  async listWorkspaces(companyId) {
    // Só dedup em voo (ttl 0): o grid remonta 2x na mesma carga (gate de
    // loading do EmpresaDashboard) e disparava o GET 2x; o preview por membro
    // é semi-volátil, então não servimos resposta velha — apenas colapsamos
    // chamadas simultâneas na mesma promise.
    return requestCache.cachedGet(
      `company:${companyId}:workspaces`,
      async () => {
        const response = await api.get(`/api/companies/${companyId}/workspaces`);
        return response.data;
      },
      { ttl: 0 },
    );
  },

  /**
   * Enter another user's workspace (Etapa 2.2 — delegation).
   * Returns { token, target: { user_id, name }, actor: { user_id, name } }.
   * The caller swaps localStorage.token for the returned token.
   */
  async enterWorkspace(targetUserId, contactPhone = null) {
    const response = await api.post('/api/companies/delegation', {
      target_user_id: targetUserId,
      // Fase 4 (item B): escopo de funil — autoriza abrir ESTE contato de um
      // funil liberado em modo "all", mesmo sem allowlist de workspace.
      ...(contactPhone ? { contact_phone: contactPhone } : {}),
    });
    return response.data?.data;
  },

  /**
   * Get workspace access config (Fase 1 — governança). Owner or admin only.
   * Returns { mode: 'private'|'specific', allowed_member_ids: [str] }.
   */
  async getWorkspaceAccess(companyId, memberUserId) {
    const response = await api.get(
      `/api/companies/${companyId}/workspaces/${memberUserId}/access`,
    );
    return response.data?.data;
  },

  /**
   * Set workspace access config (Fase 1 — governança). Owner or admin only.
   * @param cfg - { mode: 'private'|'specific', allowed_member_ids: [str] }
   */
  async setWorkspaceAccess(companyId, memberUserId, cfg) {
    const response = await api.put(
      `/api/companies/${companyId}/workspaces/${memberUserId}/access`,
      cfg,
    );
    bustCompanyCache();
    return response.data?.data;
  },

  /**
   * Funcionalidade A — concessão viewer-cêntrica: quais workspaces este MEMBRO
   * pode acessar (por categoria e/ou membros específicos). Admin-only.
   * Returns { mode: 'none'|'category'|'specific', category_ids, member_ids }.
   */
  async getWorkspaceGrants(companyId, memberUserId) {
    const response = await api.get(
      `/api/companies/${companyId}/members/${memberUserId}/workspace-grants`,
    );
    return response.data?.data;
  },

  async setWorkspaceGrants(companyId, memberUserId, cfg) {
    const response = await api.put(
      `/api/companies/${companyId}/members/${memberUserId}/workspace-grants`,
      cfg,
    );
    bustCompanyCache();
    return response.data?.data;
  },

  /**
   * Funcionalidade B — designa o membro como operador SEM número próprio
   * (lê o inbox virtual, responde pelo número da equipe). Admin-only.
   */
  async setMemberNoNumber(companyId, memberUserId, value) {
    const response = await api.put(
      `/api/companies/${companyId}/members/${memberUserId}/no-number`,
      { operates_without_number: !!value },
    );
    return response.data?.data;
  },

  /**
   * Conta de suporte: nunca entra em roleta nem em distribuição automática
   * de leads (flag SUPPORT_ACCOUNT_EXCLUSION_ENABLED). Admin-only.
   */
  async setMemberSupport(companyId, memberUserId, value) {
    const response = await api.put(
      `/api/companies/${companyId}/members/${memberUserId}/support`,
      { is_support: !!value },
    );
    return response.data?.data;
  },

  /**
   * Permissões extras do membro (2026-10-10). `edit_agents`: o membro pode
   * editar o agente compartilhado (desenvolvedor). Admin-only; vale com o
   * feature governance_lockdown.
   */
  async setMemberPermissions(companyId, memberUserId, permissions) {
    const response = await api.put(
      `/api/companies/${companyId}/members/${memberUserId}/permissions`,
      permissions || {},
    );
    return response.data?.data;
  },

  /**
   * Create a new member of the company (admin-only).
   * Throws with code 'seat_limit_reached' when limit is hit.
   */
  async addMember(companyId, { name, email, password, confirmExtraSeat = false, sendCredentialsEmail = false }) {
    // confirmExtraSeat: o admin viu e aceitou o valor da cadeira adicional
    // (usuário além do incluído no plano). Sem ele, o backend devolve
    // seat_purchase_required e nada é cobrado.
    // sendCredentialsEmail (2026-10-14): o backend manda login e senha ao
    // membro por e-mail; a resposta traz credentials_email_sent.
    const response = await api.post(`/api/companies/${companyId}/members`, {
      name,
      email,
      password,
      confirm_extra_seat: !!confirmExtraSeat,
      send_credentials_email: !!sendCredentialsEmail,
    });
    const member = response.data?.data;
    if (member && typeof member === 'object') {
      member.credentials_email_sent = !!response.data?.credentials_email_sent;
    }
    return member;
  },

  /**
   * Cadeiras da empresa: incluídas no plano, cortesia, pagas, em uso e se dá
   * para comprar (null com a flag company_seats_billing desligada).
   */
  async getSeats(companyId) {
    const response = await api.get(`/api/companies/${companyId}/seats`);
    return response.data?.data || null;
  },

  /**
   * Suspend or reactivate a member.
   * @param status - 'active' | 'suspended'
   */
  async setMemberStatus(companyId, memberUserId, status) {
    const response = await api.patch(
      `/api/companies/${companyId}/members/${memberUserId}/status`,
      { account_status: status },
    );
    return response.data?.data;
  },

  /**
   * Remove member from the company (hard delete). Os LEADS do membro (4
   * canais, com o CRM) vao antes para `transferTo` (default: o admin).
   */
  async removeMember(companyId, memberUserId, transferTo = null) {
    const params = transferTo
      ? `?transfer_to=${encodeURIComponent(transferTo)}` : '';
    const response = await api.delete(
      `/api/companies/${companyId}/members/${memberUserId}${params}`,
    );
    return response.data?.data;
  },

  /**
   * Assistente de saída (flag member_offboarding_wizard): quantos leads a
   * transferência moveria, por canal. { member, admin_id, total, por_canal }.
   */
  async getMemberLeadsSummary(companyId, memberUserId) {
    const response = await api.get(
      `/api/companies/${companyId}/members/${memberUserId}/leads/summary`,
    );
    return response.data?.data;
  },

  /**
   * Move os leads do membro SEM removê-lo. plan = { mode: 'admin' } |
   * { mode: 'member', member_id } | { mode: 'roulette', weights: {id: peso} }.
   * Devolve o resumo por canal e por destino.
   */
  async transferMemberLeads(companyId, memberUserId, plan) {
    const response = await api.post(
      `/api/companies/${companyId}/members/${memberUserId}/leads/transfer`,
      plan,
    );
    bustCompanyCache();
    return response.data?.data;
  },

  /**
   * Papel do membro: 'member' | 'manager' (Gestor). Admin nao e atribuivel.
   */
  async setMemberRole(companyId, memberUserId, role) {
    const response = await api.patch(
      `/api/companies/${companyId}/members/${memberUserId}/role`,
      { role },
    );
    return response.data?.data;
  },

  /**
   * Override individual de acesso aos paineis. Campos None herdam do papel.
   * grant = { manager_dash, sales_dash, view_members: 'own'|'all'|[ids] }
   */
  async setDashboardGrant(companyId, memberUserId, grant) {
    const response = await api.patch(
      `/api/companies/${companyId}/members/${memberUserId}/dashboard-grant`,
      grant,
    );
    return response.data?.data;
  },

  /**
   * Set the shared agent for the company (Fase 3 — UI plug-in).
   */
  async setSharedAgent(companyId, agentId) {
    const response = await api.patch(`/api/companies/${companyId}/agent`, {
      agent_id: agentId,
    });
    bustCompanyCache();
    return response.data?.data;
  },

  // ---- Categorias de equipe (membros + agente) ----
  async listCategories(companyId) {
    // Catálogo quase-imutável (≤5 grupos criados pelo admin); as mutações
    // abaixo invalidam via bustCompanyCache.
    return requestCache.cachedGet(
      `company:${companyId}:categories`,
      async () => {
        const response = await api.get(`/api/companies/${companyId}/categories`);
        return response.data?.data || [];
      },
      { ttl: 5 * 60_000 },
    );
  },

  async createCategory(companyId, { title, description }) {
    const response = await api.post(`/api/companies/${companyId}/categories`, {
      title,
      description: description || null,
    });
    bustCompanyCache();
    return response.data?.data;
  },

  async deleteCategory(companyId, categoryId) {
    const response = await api.delete(
      `/api/companies/${companyId}/categories/${categoryId}`,
    );
    bustCompanyCache();
    return response.data?.data;
  },

  async setMemberCategory(companyId, memberUserId, categoryId) {
    const response = await api.put(
      `/api/companies/${companyId}/members/${memberUserId}/category`,
      { category_id: categoryId || null },
    );
    bustCompanyCache();
    return response.data?.data;
  },

  async setAgentCategory(companyId, categoryId) {
    const response = await api.put(
      `/api/companies/${companyId}/agent-category`,
      { category_id: categoryId || null },
    );
    bustCompanyCache();
    return response.data?.data;
  },

  // ---- Roleta de transferência POR AGENTE (Plano 2 — por agente) ----
  /** Config da roleta de um agente. Retorna
   *  { agent_id, target_category_id, mode, fixed_member_id, weights:{member_id:int} }. */
  async getAgentTransferConfig(companyId, agentId) {
    const response = await api.get(
      `/api/companies/${companyId}/agents/${agentId}/transfer-config`,
    );
    return response.data?.data;
  },

  /** Grava a roleta de um agente. As chaves de `weights` definem o pool. */
  async setAgentTransferConfig(
    companyId,
    agentId,
    { target_category_id, mode, fixed_member_id, target_agent_id, weights },
  ) {
    const response = await api.put(
      `/api/companies/${companyId}/agents/${agentId}/transfer-config`,
      {
        target_category_id: target_category_id ?? null,
        mode: mode || 'round_robin',
        fixed_member_id: fixed_member_id ?? null,
        target_agent_id: target_agent_id ?? null,
        weights: weights || {},
      },
    );
    bustCompanyCache();
    return response.data?.data;
  },

  // ---- Ponte do Gestor (gestor_bridge) ----
  /** Config atual da Ponte do Gestor (admin). Retorna
   *  { enabled, gestor_agent_id, trigger_events, internal_members, available_members }. */
  async getGestorBridge(companyId) {
    const response = await api.get(`/api/companies/${companyId}/gestor-bridge`);
    return response.data?.data;
  },

  /** Grava a Ponte do Gestor (admin). `memberUserIds` = equipe interna;
   *  `weights` = peso 1-100 por membro no round-robin; `triggerEvents` = tipos
   *  de evento que disparam a notificação ([] = todos). */
  async setGestorBridge(companyId, { enabled, gestorAgentId, memberUserIds, triggerEvents, weights, manualMembers, hostMemberIds, routes }) {
    const response = await api.put(`/api/companies/${companyId}/gestor-bridge`, {
      // destino por tipo de aviso {tipo: [ids]}; ausente = backend preserva
      ...(routes ? { routes } : {}),
      enabled: !!enabled,
      gestor_agent_id: gestorAgentId ?? null,
      member_user_ids: memberUserIds || [],
      trigger_events: triggerEvents || [],
      weights: weights || {},
      // acessos avulsos por número (não são membros/asset): [{name, phone}]
      manual_members: manualMembers || [],
      // números em que o Gestor atende ([] = todos, comportamento antigo)
      host_member_ids: hostMemberIds || [],
    });
    bustCompanyCache();
    return response.data?.data;
  },

  // ---- Governança de funis (Fase 4 — item B) ----
  /** Funis do admin (definições espelhadas) — usado na config por membro.
   *  MESMO endpoint do authService.listFunnels — compartilha a mesma chave
   *  de cache para não duplicar o GET. */
  async listFunnels() {
    return requestCache.cachedGet(
      'funnels:catalog',
      async () => {
        const response = await api.get('/api/pipelines');
        return response.data?.data || [];
      },
      { ttl: 5 * 60_000 },
    );
  },

  /** Mapa { funnel_id: { member_id: 'own'|'all' } } (admin-only). */
  async getFunnelAccess(companyId) {
    const response = await api.get(`/api/companies/${companyId}/funnel-access`);
    return response.data?.data || {};
  },

  /** Define o escopo de um membro p/ um ou mais funis ('ou mais'). */
  async setFunnelAccess(companyId, { memberUserId, funnelIds, scope }) {
    const response = await api.put(`/api/companies/${companyId}/funnel-access`, {
      member_user_id: memberUserId,
      funnel_ids: funnelIds,
      scope,
    });
    bustCompanyCache();
    return response.data?.data;
  },

  /** Mapa { member_id: {agent_id, autopilot_mode, autopilot_schedule} } (admin). */
  async getMemberAgents(companyId) {
    const response = await api.get(`/api/companies/${companyId}/member-agents`);
    return response.data?.data || {};
  },

  /** Define o agente fixo + autopilot/horário do número de um membro (item E).
   *  useFixedNumber: true = o agente responde/greeting pelo número FIXO deste
   *  membro; false = CONTINUA pelo número de origem da thread (ex.: SDR/admin). */
  async setMemberAgent(companyId, { memberUserId, agentId, autopilotMode, autopilotSchedule, transferGreeting, handoffTemplate, useFixedNumber }) {
    const response = await api.put(`/api/companies/${companyId}/member-agents`, {
      member_user_id: memberUserId,
      agent_id: agentId ?? null,
      autopilot_mode: autopilotMode ?? null,
      autopilot_schedule: autopilotSchedule ?? null,
      transfer_greeting: transferGreeting ?? null,
      ...(useFixedNumber !== undefined ? { use_fixed_number: useFixedNumber } : {}),
      // undefined => não toca; {name, language} ou {} (limpa) => grava
      ...(handoffTemplate !== undefined ? { handoff_template: handoffTemplate } : {}),
    });
    bustCompanyCache();
    return response.data?.data;
  },

  /** Templates Business APROVADOS do número de um membro (alimenta o seletor de
   *  mensagem de abertura). { data: [...], available, error }. */
  async getMemberTemplates(companyId, memberUserId) {
    const response = await api.get(
      `/api/companies/${companyId}/members/${memberUserId}/templates`
    );
    return response.data || { data: [], available: false };
  },
};

export default companyService;
